import React, { ReactElement, useEffect, useState } from "react";
import { gql, useQuery } from "urql";
import { useParams } from "wouter";
import { Document, Page } from "react-pdf";
import { pdfjs } from "react-pdf";
import { Download } from "lucide-react";
import save from "save-file";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const getAttachment = gql`
    query getAttachment($_id: GraphQLObjectId!) {
        attachment(_id: $_id) {
            fileId
            fileName
            base64
            mimeType
        }
    }
`;

export const useContainerWidth = (): [(node: HTMLDivElement | null) => void, number] => {
    const [el, setEl] = useState<HTMLDivElement | null>(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        if (!el) {
            return;
        }
        const updateWidth = (): void => {
            setWidth(Math.floor(el.clientWidth));
        };
        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(el);
        return () => observer.disconnect();
    }, [el]);

    return [setEl, width];
};

export const PdfViewPage = (): ReactElement => {
    const { id }: any = useParams();
    const [numPages, setNumPages] = useState(null);
    const [containerRef, containerWidth] = useContainerWidth();

    const [{ fetching, error, data }] = useQuery({ query: getAttachment, variables: { _id: id } });

    if (fetching) return <p>Loading</p>;
    if (error) return <p>Error: {JSON.stringify(error, null, 2)}</p>;

    const attachment = data.attachment;
    const dataURI = "data:" + attachment.mimeType + ";base64," + attachment.base64;

    const handleDownload = () => {
        save(dataURI, attachment.fileName.replaceAll(" ", "_")).catch(console.error);
    };

    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
    }

    const allPages = [...Array(numPages + 1).keys()].slice(1);

    return (
        <div className="w-full max-w-screen-2xl mx-auto pb-4">
            <div className="mb-4 d-flex justify-content-end">
                <button type="button" className="btn btn-primary mr-3" onClick={handleDownload}>
                    <Download size={16} className="mr-2" />
                    Download
                </button>
            </div>
            <div className="w-full min-w-0 overflow-x-hidden">
                <div ref={containerRef} className="h-0 w-full overflow-hidden" />
                {containerWidth > 0 ? (
                    <Document file={dataURI} onLoadSuccess={onDocumentLoadSuccess}>
                        {allPages.map((page, ndx) => (
                            <Page key={`page${ndx}`} pageNumber={page} width={containerWidth} className="mb-4 shadow-md" />
                        ))}
                    </Document>
                ) : null}
            </div>
        </div>
    );
};
